<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import { DatePicker, FilterModal, IconImage, SelectInput } from '@/shared/ui';
  import {
    getNoticeCategoryLabel,
    NOTICE_CATEGORY_LABELS,
    type NoticeCategory,
  } from '@/entities/ReminderHistory';
  import { membersApi, type BranchMember } from '@/entities/Branch';
  import type { ReminderHistoryFilters } from '../model/types';

  interface SelectOption {
    id: string | number;
    name: string;
  }

  const CATEGORIES = Object.keys(NOTICE_CATEGORY_LABELS) as NoticeCategory[];

  const props = defineProps<{
    currentFilters?: ReminderHistoryFilters;
    showRecipientFilter?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'update:filter', filters: ReminderHistoryFilters): void;
  }>();

  const members = ref<BranchMember[]>([]);

  const createdAt = ref<Date | null>(null);
  const createdBy = ref<string | number>('');
  const recipientId = ref<string | number>('');
  const category = ref<NoticeCategory | ''>('');
  const isDateOpen = ref(false);

  const usersOptions = computed<SelectOption[]>(() =>
    members.value
      .filter((m) => typeof m.id === 'number' && Boolean(m.user?.name))
      .map((m) => ({
        id: m.id as number,
        name: m.user?.name ?? '',
      }))
  );

  const categoryOptions = computed<SelectOption[]>(() =>
    CATEGORIES.map((currentCategory) => ({
      id: currentCategory,
      name: getNoticeCategoryLabel(currentCategory),
    }))
  );

  const dateButtonLabel = computed(() => {
    if (!createdAt.value) {
      return 'Selecione uma data';
    }

    return createdAt.value.toLocaleDateString('pt-BR');
  });

  const parseDate = (value?: string): Date | null => {
    if (!value) {
      return null;
    }

    const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));

      if (
        parsedDate.getFullYear() !== Number(year) ||
        parsedDate.getMonth() !== Number(month) - 1 ||
        parsedDate.getDate() !== Number(day)
      ) {
        return null;
      }

      return parsedDate;
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate;
  };

  const getRecipientFilterValue = (filters?: ReminderHistoryFilters) => {
    if (!filters || !('recipientId' in filters)) {
      return '';
    }

    return filters.recipientId ?? '';
  };

  const getCurrentRecipientFilter = () => {
    const currentFilters = props.currentFilters;

    if (!currentFilters || !('recipientId' in currentFilters)) {
      return undefined;
    }

    return currentFilters.recipientId;
  };

  const loadFilters = (filters?: ReminderHistoryFilters) => {
    createdAt.value = parseDate(filters?.createdAt);
    createdBy.value = filters?.createdBy ?? '';
    recipientId.value = getRecipientFilterValue(filters);
    category.value = filters?.category ?? '';
  };

  const formatDateForQuery = (date: Date | null) => {
    if (!date) {
      return undefined;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const normalizeSelectedUserId = (value: string | number) => {
    if (value === '') {
      return undefined;
    }

    const parsedValue = Number(value);

    if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
      return undefined;
    }

    return parsedValue;
  };

  const clearFilters = () => {
    createdAt.value = null;
    createdBy.value = '';
    recipientId.value = '';
    category.value = '';
  };

  const applyFilters = () => {
    const normalizedRecipientId = normalizeSelectedUserId(recipientId.value);
    const nextFilters: ReminderHistoryFilters = {
      createdAt: formatDateForQuery(createdAt.value),
      createdBy: normalizeSelectedUserId(createdBy.value),
      category: category.value === '' ? undefined : category.value,
      ...(props.showRecipientFilter && normalizedRecipientId !== undefined
        ? { recipientId: normalizedRecipientId }
        : {}),
    };

    emit('update:filter', nextFilters);
  };

  onMounted(async () => {
    members.value = await membersApi.getAllMembersPaginated(100, undefined, undefined, {
      includeInactiveMembers: true,
    });
  });

  watch(
    () =>
      [
        props.currentFilters?.createdAt,
        props.currentFilters?.createdBy,
        props.currentFilters?.category,
        props.showRecipientFilter,
        getCurrentRecipientFilter(),
      ] as const,
    () => {
      loadFilters(props.currentFilters);
    },
    { immediate: true }
  );
</script>

<template>
  <FilterModal
    :buttons-text="{ clear: 'Limpar filtros', apply: 'Aplicar filtros' }"
    :content-class="'w-85'"
    @close="emit('close')"
    @clear-filters="clearFilters"
    @apply-filters="applyFilters"
  >
    <template #content>
      <div class="divide-y divide-gray-02">
        <div class="space-y-2 px-6 py-6">
          <h4 class="text-sm font-medium text-gray-07">
            Data de envio
          </h4>
          <button
            type="button"
            class="flex items-center rounded-lg border border-gray-02 bg-white-01 px-4 py-2.5 text-gray-03"
            @click="isDateOpen = true"
          >
            <IconImage
              :src="CalendarIcon"
              alt="Calendário"
              class="h-5 w-5"
            />
            <span
              class="grow text-center text-sm"
              :class="createdAt ? 'text-gray-07' : 'text-gray-03'"
            >
              {{ dateButtonLabel }}
            </span>
          </button>
          <DatePicker
            v-model:selected-date="createdAt"
            v-model:is-open="isDateOpen"
            @apply="isDateOpen = false"
            @cancel="isDateOpen = false"
          />
        </div>

        <div class="space-y-2 px-6 py-6">
          <h4 class="text-sm font-medium text-gray-07">
            Colaborador remetente
          </h4>
          <SelectInput
            :model-value="createdBy"
            :options="usersOptions"
            label="Selecione um colaborador"
            :use-teleport="true"
            @update:model-value="createdBy = $event"
          />
        </div>

        <div
          v-if="props.showRecipientFilter"
          class="space-y-2 px-6 py-6"
        >
          <h4 class="text-sm font-medium text-gray-07">
            Enviado para
          </h4>
          <SelectInput
            :model-value="recipientId"
            :options="usersOptions"
            label="Selecione um colaborador"
            :use-teleport="true"
            @update:model-value="recipientId = $event"
          />
        </div>

        <div class="space-y-2 px-6 py-6">
          <h4 class="text-sm font-medium text-gray-07">
            Categoria
          </h4>
          <SelectInput
            :model-value="category"
            :options="categoryOptions"
            label="Selecione uma categoria"
            :use-teleport="true"
            @update:model-value="category = $event as NoticeCategory | ''"
          />
        </div>
      </div>
    </template>
  </FilterModal>
</template>
